'use client'

/**
 * An app's LIVE brand tile from the multipass registry (icon + colors set in
 * HQ → Apps), for surfaces that render signed out: the sign-in card and its
 * tab favicon. Signed-in surfaces read the same rows through `useOrdoApps`,
 * which needs a session; this reads multipass's public
 * `GET /api/apps/:slug/brand` instead.
 *
 * `unavailable` means "draw your built-in brand": the lookup failed, the slug
 * is unknown or retired, or the row has no complete tile. Ready brands are
 * remembered per origin in localStorage so a returning visitor's first paint
 * is already the live tile, then revalidated in the background.
 */
import { useCallback, useSyncExternalStore } from 'react'

import { sameEnvOrigin } from '../same-env-origin'

export interface RegistryAppBrand {
  slug: string
  name: string
  /** Phosphor icon component name (e.g. "MapTrifold"). */
  icon: string
  /** Hex tile background color. */
  bgColor: string
  /** Hex tile icon color. */
  fgColor: string
}

export type AppBrandState =
  | { status: 'loading' }
  | { status: 'ready'; brand: RegistryAppBrand }
  | { status: 'unavailable' }

const LOADING: AppBrandState = { status: 'loading' }
const UNAVAILABLE: AppBrandState = { status: 'unavailable' }

// Multipass, retargeted onto the page's own preview ring (`auth.<env>…`).
const DEFAULT_MULTIPASS_URL = 'https://auth.ordo.com'
// Matches the endpoint's `max-age`: how long a loaded brand is trusted.
const TTL_MS = 60 * 1000
const TIMEOUT_MS = 4000
const STORAGE_PREFIX = 'ordo:app-brand:'
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i

interface Entry {
  state: AppBrandState
  fetchedAt: number
  inFlight: Promise<void> | null
}

const entries = new Map<string, Entry>()
const listeners = new Map<string, Set<() => void>>()

/**
 * The tile in a `/api/apps/:slug/brand` body, or null unless it has a name,
 * an icon and two hex colors. The colors land inside favicon SVG markup, so
 * anything that isn't a plain hex color is refused. Pure; exported for tests.
 */
export function parseAppBrand(body: unknown): RegistryAppBrand | null {
  if (!body || typeof body !== 'object') {
    return null
  }
  const { slug, name, icon, bgColor, fgColor } = body as Record<string, unknown>
  if (typeof slug !== 'string' || typeof name !== 'string' || typeof icon !== 'string' || !icon) {
    return null
  }
  if (typeof bgColor !== 'string' || !HEX_COLOR.test(bgColor)) {
    return null
  }
  if (typeof fgColor !== 'string' || !HEX_COLOR.test(fgColor)) {
    return null
  }
  return { slug, name, icon, bgColor, fgColor }
}

function readStored(slug: string): RegistryAppBrand | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_PREFIX + slug)
    return raw ? parseAppBrand(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

function writeStored(slug: string, brand: RegistryAppBrand | null): void {
  try {
    if (brand) {
      globalThis.localStorage?.setItem(STORAGE_PREFIX + slug, JSON.stringify(brand))
    } else {
      globalThis.localStorage?.removeItem(STORAGE_PREFIX + slug)
    }
  } catch {
    // Storage blocked (private mode, quota) — the in-memory entry still works.
  }
}

function entryFor(slug: string): Entry {
  let entry = entries.get(slug)
  if (!entry) {
    const stored = readStored(slug)
    entry = { state: stored ? { status: 'ready', brand: stored } : LOADING, fetchedAt: 0, inFlight: null }
    entries.set(slug, entry)
  }
  return entry
}

function setState(slug: string, state: AppBrandState): void {
  entryFor(slug).state = state
  for (const listener of listeners.get(slug) ?? []) {
    listener()
  }
}

/** A failed lookup keeps a brand already on screen; only a 404 retires it. */
function settle(slug: string, brand: RegistryAppBrand | null, retired: boolean): void {
  const current = entryFor(slug).state
  if (brand) {
    writeStored(slug, brand)
    setState(slug, { status: 'ready', brand })
    return
  }
  if (retired) {
    writeStored(slug, null)
    setState(slug, UNAVAILABLE)
    return
  }
  if (current.status === 'loading') {
    setState(slug, UNAVAILABLE)
  }
}

function load(slug: string): Promise<void> {
  const entry = entryFor(slug)
  if (entry.inFlight) {
    return entry.inFlight
  }
  const url = `${sameEnvOrigin(DEFAULT_MULTIPASS_URL)}/api/apps/${encodeURIComponent(slug)}/brand`
  entry.inFlight = fetch(url, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    // A hung lookup must not hold the card on its skeleton (pre-2022 browsers lack timeout()).
    ...(typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(TIMEOUT_MS) } : {}),
  })
    .then(async (res) => {
      entry.fetchedAt = Date.now()
      if (res.status === 404) {
        settle(slug, null, true)
        return
      }
      // A 200 whose row has no complete tile is "no live brand", like a 404.
      settle(slug, res.ok ? parseAppBrand(await res.json()) : null, res.ok)
    })
    .catch(() => {
      settle(slug, null, false)
    })
    .finally(() => {
      entry.inFlight = null
    })
  return entry.inFlight
}

export function getAppBrandSnapshot(slug: string | null): AppBrandState {
  return slug ? entryFor(slug).state : UNAVAILABLE
}

export function subscribeAppBrand(slug: string | null, listener: () => void): () => void {
  if (!slug) {
    return () => {}
  }
  let set = listeners.get(slug)
  if (!set) {
    set = new Set()
    listeners.set(slug, set)
  }
  set.add(listener)
  const entry = entryFor(slug)
  if (Date.now() - entry.fetchedAt > TTL_MS) {
    void load(slug)
  }
  return () => {
    set.delete(listener)
  }
}

/** The live registry tile for `slug` (an `AppBrand.registrySlug`). */
export function useAppBrand(slug: string | null): AppBrandState {
  const subscribe = useCallback((listener: () => void) => subscribeAppBrand(slug, listener), [slug])
  const getSnapshot = useCallback(() => getAppBrandSnapshot(slug), [slug])
  // SSR has no storage or network: render the loading state, never a guess.
  const getServerSnapshot = useCallback(() => (slug ? LOADING : UNAVAILABLE), [slug])
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
